import { Chart, ChartConfiguration } from 'chart.js';

import { cssColor } from '../../utils/theme-colors';

import { baseDescription } from './cards/card-statement-parser';
import { CardItemModel } from './finance.model';
import { round2 } from './finance-summary.service';

// Charts of credit card items, shared by the Overview and the Credit Card screens

export type PieData = ChartConfiguration<'pie'>['data'];
export type BarData = ChartConfiguration<'bar'>['data'];

// Chart colors of the theme (theme.scss), usable in templates as they are
export const PALETTE = [1, 2, 3, 4, 5, 6, 7, 8].map(n => `var(--app-c${n})`);
const TOP_DESCRIPTIONS = 10;

export interface ItemGroup {
  key: string,
  value: number,
  count: number
}

export function groupBy(items: CardItemModel[], key: (item: CardItemModel) => string): ItemGroup[] {
  const groups = new Map<string, ItemGroup>();
  for (const item of items) {
    const k = key(item);
    const group = groups.get(k) ?? { key: k, value: 0, count: 0 };
    group.value = round2(group.value + (Number(item.value) || 0));
    group.count++;
    groups.set(k, group);
  }
  return [...groups.values()].sort((a, b) => b.value - a.value);
}

export const NO_CATEGORY = 'Sem categoria';

// Totals by category, largest first, with the color of its slice in the pie
export function categoryTotals(items: CardItemModel[]): (ItemGroup & { color: string | null })[] {
  let slice = 0;
  // Credits (negative totals) have no slice, so no color
  return groupBy(items, i => i.category || NO_CATEGORY)
    .map(g => ({ ...g, color: g.value > 0 ? PALETTE[slice++ % PALETTE.length] : null }));
}

export function byCategoryChart(items: CardItemModel[]): PieData | null {
  const groups = categoryTotals(items).filter(g => g.color);
  return groups.length ? {
    labels: groups.map(g => g.key),
    datasets: [{ data: groups.map(g => g.value), backgroundColor: groups.map(g => cssColor(g.color!)), borderColor: cssColor('var(--app-surface)') }]
  } : null;
}

// Repeated purchases (same place, installments of the same purchase) are added together
export function byDescriptionChart(items: CardItemModel[]): { data: BarData, groups: ItemGroup[] } | null {
  const groups = groupBy(items, i => baseDescription(i.description)).filter(g => g.value > 0);
  const top = groups.slice(0, TOP_DESCRIPTIONS);
  const others = groups.slice(TOP_DESCRIPTIONS);
  if (others.length)
    top.push({ key: 'Outros', value: round2(others.reduce((total, g) => total + g.value, 0)), count: others.reduce((total, g) => total + g.count, 0) });

  return top.length ? {
    groups: top,
    data: { labels: top.map(g => g.key), datasets: [{ data: top.map(g => g.value), backgroundColor: cssColor('var(--app-c3)') }] }
  } : null;
}

// Texts and grid lines of every chart follow the theme; call before building the chart data
export function applyChartTheme(): void {
  Chart.defaults.color = cssColor('var(--app-muted)');
  Chart.defaults.borderColor = cssColor('var(--app-border)');
}

export const PIE_OPTIONS: ChartConfiguration<'pie'>['options'] = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'right' },
    tooltip: { callbacks: { label: ctx => `${ctx.label}: ${brl(ctx.parsed)} (${percentOf(ctx.parsed, ctx.dataset.data as number[])})` } }
  }
};

// The groups give the number of purchases of each bar to the tooltip
export function barOptions(groups: () => ItemGroup[]): ChartConfiguration<'bar'>['options'] {
  return {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: ctx => {
            const count = groups()[ctx.dataIndex]?.count ?? 1;
            return `${brl(ctx.parsed.x ?? 0)}${count > 1 ? ` (${count} compras)` : ''}`;
          }
        }
      }
    }
  };
}

export function brl(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function percentOf(value: number, values: number[]): string {
  const total = values.reduce((sum, v) => sum + v, 0);
  return total ? `${(value / total * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%` : '0%';
}
